import { ApolloClient } from 'apollo-client'
import { ApolloLink } from 'apollo-link'
import { createHttpLink } from 'apollo-link-http'
import { onError } from 'apollo-link-error'
import { RetryLink } from 'apollo-link-retry'
import {
  InMemoryCache,
  IntrospectionFragmentMatcher
} from 'apollo-cache-inmemory'
import introspectionQueryResultData from './fragmentTypes.json'
import { trackGraphQLErrors, trackNetworkError } from './utils/errors'
import { browserHistory } from 'react-router'
import { loginPath } from 'lib/path-helpers'
import { toaster } from 'evergreen-v4'
import request from 'lib/request'
import { get } from 'lodash'
import OmnisearchClient from 'lib/omnisearch'

const httpLink = createHttpLink({
  uri: '/api/graphql',
  credentials: 'include',
  fetch: request
})

/*
  Add ability to retry different sort of GraphQL errors.
  While this is a really awesome idea, we won't be able to retry semantic
  GraphQL errors, but only Network errors that contain a status code, given that
  `retry-link` will only apply to > 4xx error responses.
*/
const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true
  },
  attempts: {
    max: 3,
    retryIf: async (error, operation) => {
      const operationType = get(
        operation,
        'query.definitions[0].operation',
        false
      )
      const isQuery = operationType === 'query'

      // We want to make sure mutations don't get retried
      if (!isQuery) {
        return false
      }

      // Only retry network errors and 5xx errors,
      // not 401 errors caused by invalid/expired auth tokens
      if (!error.statusCode || error.statusCode >= 500) {
        return true
      }

      return false
    }
  }
})

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData
})

const cache = new InMemoryCache({ fragmentMatcher })

function logoutUser() {
  const location = window.location.toString()
  const loggedOutPaths = [
    'login',
    'signup',
    'forgot',
    'reset',
    'enable',
    'logout'
  ]

  const isLoggedOutPage = loggedOutPaths.some(p => location.includes(p))
  if (isLoggedOutPage) {
    return
  }

  toaster.warning(
    'Your session has expired. Please re-enter your login credentials',
    10000
  )

  /*
    Resets the client so we're not longer having to deal
    with redirects because invalid cache data is available
  */
  return cache.reset().then(() => {
    const path = loginPath({
      query: {
        redirect: window.location.pathname
      }
    })

    browserHistory.push(path)
  })
}

const mutationDetectionLink = new ApolloLink((operation, forward) => {
  const operationType = get(operation, 'query.definitions[0].operation')
  const isMutation = operationType === 'mutation'

  const operationName = get(operation, 'query.definitions[0].name.value')
  if (isMutation) {
    setTimeout(
      () => OmnisearchClient.debouncedRefreshIndices(operationName),
      1000
    )
  }

  return forward(operation)
})

// This function can't be async or return a Promise because it will break all network error handling
// https://github.com/apollographql/apollo-link/tree/c5e55c03b7bdc9d2b44b609ce2dc98661b3ed82e/packages/apollo-link-error#callback
function graphqlErrorHandler(args) {
  const { graphQLErrors, networkError, operation } = args

  if (graphQLErrors) {
    trackGraphQLErrors(graphQLErrors, operation.operationName)
  }

  if (networkError) {
    if (networkError.statusCode === 401) {
      logoutUser()
    }

    trackNetworkError(networkError, operation.operationName)
  }
}

const errorHandler = onError(graphqlErrorHandler)

export default new ApolloClient({
  link: errorHandler
    .concat(retryLink)
    .concat(mutationDetectionLink)
    .concat(httpLink),
  cache,
  queryDeduplication: true,
  connectToDevTools: true, // Enable Apollo Devtools in production
  fragmentMatcher: new IntrospectionFragmentMatcher({
    introspectionQueryResultData
  })
})
